import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  Send as SendIcon,
  SmartToy as SmartToyIcon,
} from "@mui/icons-material";
import { useAuth } from "@/app/providers";
import { useAreaPermissions } from "@/contexts/AreaPermissionsContext";
import { API_URL } from "@/shared/config";
import { apiService } from "@/shared/api";
import ChatMarkdown from "./ChatMarkdown";
import TokenQuotaRing, { type TokenQuota } from "./TokenQuotaRing";

type ChatRole = "user" | "assistant";

interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
}

interface HistoryMessageApi {
  pk: number;
  prompt: string;
  ia_response: boolean;
  created_at?: string;
}

interface HistoryConvApi {
  pk: number;
  conv_name?: string;
  messages?: HistoryMessageApi[];
  updated_at?: string;
}

interface HistoryConvListResponse {
  status: string;
  fields?: HistoryConvApi[];
  field?: HistoryConvApi;
  error?: string;
}

interface TokenUserIAApi {
  pk: number;
  pk_user: number;
  token_available: number;
  token_used: number;
}

interface TokenUserIAResponse {
  status: string;
  fields?: TokenUserIAApi[];
  field?: TokenUserIAApi;
  error?: string;
}

interface ChatbotResponse {
  status: string;
  reply?: string;
  conversation?: number;
  conv_name?: string;
  error?: string;
}

function nextId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

type SseHandler = {
  onToken?: (text: string) => void;
  onStatus?: (phase: string) => void;
  onDone?: (data: ChatbotResponse) => void;
  onError?: (data: { error?: string; detail?: string; http_status?: number }) => void;
};

async function postChatbotSse(
  body: Record<string, unknown>,
  token: string | undefined,
  handlers: SseHandler,
): Promise<void> {
  const res = await fetch(`${API_URL}/ai/chatbot/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `token ${token}` } : {}),
    },
    body: JSON.stringify({ ...body, stream: true }),
  });

  if (!res.ok || !res.body) {
    let errKey = `http_${res.status}`;
    try {
      const data = await res.json();
      if (data?.error) errKey = String(data.error);
    } catch {
      /* ignore */
    }
    throw Object.assign(new Error(errKey), {
      httpStatus: res.status,
      data: { error: errKey },
    });
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let sawDone = false;

  const dispatchBlock = (block: string) => {
    const lines = block.split("\n");
    let eventName = "message";
    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith("event:")) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).trim());
      }
    }
    if (!dataLines.length) return;
    let payload: any = {};
    try {
      payload = JSON.parse(dataLines.join("\n"));
    } catch {
      return;
    }
    if (eventName === "token" && typeof payload.text === "string") {
      handlers.onToken?.(payload.text);
    } else if (eventName === "status" && typeof payload.phase === "string") {
      handlers.onStatus?.(payload.phase);
    } else if (eventName === "done") {
      sawDone = true;
      handlers.onDone?.(payload as ChatbotResponse);
    } else if (eventName === "error") {
      handlers.onError?.(payload);
      throw Object.assign(new Error(payload?.error || "chatbot_error"), {
        httpStatus: payload?.http_status,
        data: payload,
      });
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) >= 0) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      if (block.trim()) dispatchBlock(block);
    }
  }
  if (buffer.trim()) dispatchBlock(buffer);
  if (!sawDone) {
    throw new Error("chatbot_stream_incomplete");
  }
}

function mapApiMessages(rows: HistoryMessageApi[] | undefined): ChatMessage[] {
  return (rows || [])
    .slice()
    .sort((a, b) => {
      const ta = a.created_at ? Date.parse(a.created_at) : 0;
      const tb = b.created_at ? Date.parse(b.created_at) : 0;
      if (ta !== tb) return ta - tb;
      return Number(a.pk) - Number(b.pk);
    })
    .map((row) => ({
      id: String(row.pk),
      role: (row.ia_response ? "assistant" : "user") as ChatRole,
      content: String(row.prompt || ""),
    }))
    .filter((row) => row.content.trim());
}

function convLabel(conv: HistoryConvApi): string {
  const name = String(conv.conv_name || "").trim();
  return name || `Conversation #${conv.pk}`;
}

const ChatbotWidget: React.FC = () => {
  const { token, currentBuilding } = useAuth();
  const { has, loading: areaLoading } = useAreaPermissions();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversations, setConversations] = useState<HistoryConvApi[]>([]);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [loadingConvs, setLoadingConvs] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [deletingPk, setDeletingPk] = useState<number | null>(null);
  const [tokenQuota, setTokenQuota] = useState<TokenQuota | null>(null);
  const [loadingQuota, setLoadingQuota] = useState(false);

  const loadTokenQuota = useCallback(async () => {
    if (!token) return;
    setLoadingQuota(true);
    try {
      const data = await apiService.get<TokenUserIAResponse>(
        `${API_URL}/ai/tokenUserIA/`,
        token,
      );
      const row =
        data?.field ||
        (Array.isArray(data?.fields) && data.fields.length > 0
          ? data.fields[0]
          : null);
      if (row) {
        setTokenQuota({
          available: Number(row.token_available) || 0,
          used: Number(row.token_used) || 0,
        });
      } else {
        setTokenQuota({ available: 0, used: 0 });
      }
    } catch {
      setTokenQuota({ available: 0, used: 0 });
    } finally {
      setLoadingQuota(false);
    }
  }, [token]);

  const loadConversations = useCallback(async () => {
    if (!token) return [];
    setLoadingConvs(true);
    try {
      const data = await apiService.get<HistoryConvListResponse>(
        `${API_URL}/ai/historyConv/`,
        token,
      );
      const rows = Array.isArray(data?.fields) ? data.fields : [];
      setConversations(rows);
      return rows;
    } catch {
      setConversations([]);
      return [];
    } finally {
      setLoadingConvs(false);
    }
  }, [token]);

  const selectConversation = useCallback(
    async (pk: number | null) => {
      setError("");
      if (pk == null) {
        setConversationId(null);
        setMessages([]);
        return;
      }
      if (!token) return;
      setConversationId(pk);
      try {
        const data = await apiService.get<HistoryConvListResponse>(
          `${API_URL}/ai/historyConv/?pk=${pk}`,
          token,
        );
        const field = data?.field;
        setMessages(mapApiMessages(field?.messages));
        if (field) {
          setConversations((prev) => {
            const exists = prev.some((row) => Number(row.pk) === Number(field.pk));
            if (exists) {
              return prev.map((row) =>
                Number(row.pk) === Number(field.pk) ? { ...row, ...field } : row,
              );
            }
            return [field, ...prev];
          });
        }
      } catch {
        setError("Impossible de charger cette conversation.");
        setMessages([]);
      }
    },
    [token],
  );

  useEffect(() => {
    if (!open || !token) return;
    let cancelled = false;
    void (async () => {
      await Promise.all([loadConversations(), loadTokenQuota()]);
      if (cancelled) return;
      // À chaque ouverture : toujours repartir sur une nouvelle conversation.
      setConversationId(null);
      setMessages([]);
      setPrompt("");
      setError("");
    })();
    return () => {
      cancelled = true;
    };
  }, [open, token, loadConversations, loadTokenQuota]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
    const timer = window.setTimeout(() => inputRef.current?.focus(), 100);
    return () => window.clearTimeout(timer);
  }, [open, messages, sending]);

  const handleClose = () => {
    setOpen(false);
    setConversationId(null);
    setMessages([]);
    setPrompt("");
    setError("");
  };

  if (!token) {
    return null;
  }

  const startNewConversation = () => {
    void selectConversation(null);
  };

  const deleteConversation = async (pk: number) => {
    if (!token || deletingPk != null || sending) return;
    const label =
      conversations.find((row) => Number(row.pk) === pk)?.conv_name?.trim() ||
      `Conversation #${pk}`;
    const ok = window.confirm(`Supprimer « ${label} » ?`);
    if (!ok) return;

    setDeletingPk(pk);
    setError("");
    try {
      const data = await apiService.delete<{ status?: string; error?: string }>(
        `${API_URL}/ai/historyConv/`,
        token,
        { pk },
      );
      if (data?.status && data.status !== "OK") {
        throw new Error(data.error || "delete_error");
      }
      setConversations((prev) => prev.filter((row) => Number(row.pk) !== pk));
      if (Number(conversationId) === pk) {
        setConversationId(null);
        setMessages([]);
      }
    } catch {
      setError("Impossible de supprimer cette conversation.");
    } finally {
      setDeletingPk(null);
    }
  };

  const sendPrompt = async () => {
    const text = prompt.trim();
    if (!text || sending) return;

    setError("");
    setPrompt("");
    setMessages((current) => [
      ...current,
      { id: nextId(), role: "user", content: text },
    ]);
    const assistantId = nextId();
    setMessages((current) => [
      ...current,
      { id: assistantId, role: "assistant", content: "" },
    ]);
    setSending(true);

    try {
      if (!currentBuilding?.pk) {
        throw new Error("building_required");
      }
      await postChatbotSse(
        {
          prompt: text,
          pkBuilding: currentBuilding.pk,
          ...(conversationId != null ? { conversation: conversationId } : {}),
        },
        token,
        {
          onToken: (chunk) => {
            setMessages((current) =>
              current.map((m) =>
                m.id === assistantId
                  ? { ...m, content: `${m.content}${chunk}` }
                  : m,
              ),
            );
          },
          onDone: (data) => {
            if (data?.status !== "OK") {
              throw Object.assign(new Error(data?.error || "chatbot_error"), {
                data,
              });
            }
            const newConvPk =
              data.conversation != null
                ? Number(data.conversation)
                : conversationId;
            if (newConvPk != null && Number.isFinite(newConvPk)) {
              setConversationId(newConvPk);
              setConversations((prev) => {
                const name =
                  String(data.conv_name || "").trim() ||
                  prev.find((row) => Number(row.pk) === newConvPk)?.conv_name ||
                  text.slice(0, 80);
                const nextRow: HistoryConvApi = {
                  pk: newConvPk,
                  conv_name: name,
                };
                const without = prev.filter(
                  (row) => Number(row.pk) !== newConvPk,
                );
                return [nextRow, ...without];
              });
            }
            if (data.reply) {
              setMessages((current) =>
                current.map((m) =>
                  m.id === assistantId
                    ? { ...m, content: String(data.reply) }
                    : m,
                ),
              );
            }
            void loadTokenQuota();
          },
        },
      );
    } catch (err) {
      const apiErr = err as Error & {
        httpStatus?: number;
        data?: { error?: string };
      };
      const errKey =
        (apiErr?.data && typeof apiErr.data === "object"
          ? apiErr.data.error
          : undefined) ||
        (err instanceof Error ? err.message : "");
      setMessages((current) => {
        const withoutAssistant = current.filter((m) => m.id !== assistantId);
        const last = withoutAssistant[withoutAssistant.length - 1];
        if (last?.role === "user" && last.content === text) {
          return withoutAssistant.slice(0, -1);
        }
        return withoutAssistant;
      });
      if (errKey === "insufficient_tokens" || apiErr.httpStatus === 402) {
        setError("Quota de tokens épuisé. Contacte un administrateur.");
        void loadTokenQuota();
      } else {
        setError(
          err instanceof Error && err.message === "building_required"
            ? "Sélectionne un bâtiment avant d’utiliser l’assistant."
            : "Impossible d’obtenir une réponse. Réessaie.",
        );
      }
    } finally {
      setSending(false);
    }
  };

  const isNewConversation = conversationId == null;

  if (areaLoading || !has.assistantIa) {
    return null;
  }

  return (
    <>
      <Fab
        color="primary"
        aria-label="Ouvrir le chatbot"
        onClick={() => setOpen(true)}
        sx={{
          position: "fixed",
          right: { xs: 16, sm: 24 },
          bottom: { xs: 16, sm: 24 },
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <SmartToyIcon />
      </Fab>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            height: { xs: "90vh", sm: 680 },
            maxHeight: "92vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <SmartToyIcon fontSize="small" />
            <Typography component="span" variant="h6" fontSize="1.05rem">
              Assistant
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TokenQuotaRing quota={tokenQuota} loading={loadingQuota} />
            <IconButton aria-label="Fermer" onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 0,
            display: "flex",
            flexDirection: "row",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            component="aside"
            sx={{
              width: { xs: 132, sm: 220 },
              flexShrink: 0,
              borderRight: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 0.5,
                px: 1.25,
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, letterSpacing: 0.02, textTransform: "uppercase" }}
              >
                Conversations
              </Typography>
              <IconButton
                aria-label="Nouvelle conversation"
                onClick={startNewConversation}
                disabled={sending}
                size="small"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", py: 0.75 }}>
              {loadingConvs ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : null}

              <ButtonBase
                onClick={startNewConversation}
                disabled={sending}
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  px: 1.5,
                  py: 1.1,
                  bgcolor: isNewConversation ? "action.selected" : "transparent",
                  borderLeft: "3px solid",
                  borderColor: isNewConversation ? "primary.main" : "transparent",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ fontWeight: isNewConversation ? 600 : 400 }}
                >
                  Nouvelle conversation
                </Typography>
              </ButtonBase>

              {!loadingConvs && conversations.length === 0 ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", px: 1.5, py: 1 }}
                >
                  Aucune conversation pour l’instant.
                </Typography>
              ) : null}

              {conversations.map((conv) => {
                const selected = Number(conversationId) === Number(conv.pk);
                const isDeleting = deletingPk === Number(conv.pk);
                return (
                  <Box
                    key={conv.pk}
                    sx={{
                      display: "flex",
                      alignItems: "stretch",
                      bgcolor: selected ? "action.selected" : "transparent",
                      borderLeft: "3px solid",
                      borderColor: selected ? "primary.main" : "transparent",
                      "&:hover": { bgcolor: "action.hover" },
                      "&:hover .conv-delete-btn": { opacity: 1 },
                    }}
                  >
                    <ButtonBase
                      onClick={() => void selectConversation(Number(conv.pk))}
                      disabled={sending || isDeleting}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "block",
                        textAlign: "left",
                        px: 1.5,
                        py: 1.1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        title={convLabel(conv)}
                        sx={{ fontWeight: selected ? 600 : 400 }}
                      >
                        {convLabel(conv)}
                      </Typography>
                    </ButtonBase>
                    <IconButton
                      className="conv-delete-btn"
                      aria-label={`Supprimer ${convLabel(conv)}`}
                      size="small"
                      disabled={sending || isDeleting}
                      onClick={(e) => {
                        e.stopPropagation();
                        void deleteConversation(Number(conv.pk));
                      }}
                      sx={{
                        alignSelf: "center",
                        mr: 0.5,
                        opacity: { xs: 1, sm: selected ? 1 : 0.35 },
                        color: "text.secondary",
                        "&:hover": {
                          color: "error.main",
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      {isDeleting ? (
                        <CircularProgress size={14} />
                      ) : (
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Box
              ref={listRef}
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 2,
                py: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 1.75,
                bgcolor: "background.default",
              }}
            >
              {messages.length === 0 && !sending ? (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {isNewConversation
                      ? "Nouvelle conversation — pose ta question."
                      : "Aucun message dans cette conversation."}
                  </Typography>
                </Box>
              ) : null}

              {messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    alignSelf:
                      message.role === "user" ? "flex-end" : "flex-start",
                    maxWidth: message.role === "assistant" ? "92%" : "85%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems:
                      message.role === "user" ? "flex-end" : "flex-start",
                    gap: 0.25,
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      px: message.role === "assistant" ? 2 : 1.5,
                      py: message.role === "assistant" ? 1.75 : 1,
                      borderRadius: 2.5,
                      bgcolor:
                        message.role === "user"
                          ? "primary.main"
                          : "background.paper",
                      color:
                        message.role === "user"
                          ? "primary.contrastText"
                          : "text.primary",
                      border:
                        message.role === "assistant" ? "1px solid" : "none",
                      borderColor: "divider",
                      boxShadow:
                        message.role === "assistant"
                          ? "0 1px 2px rgba(16, 24, 40, 0.04)"
                          : "none",
                      whiteSpace:
                        message.role === "user" ? "pre-wrap" : "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {message.role === "assistant" ? (
                      <ChatMarkdown content={message.content} />
                    ) : (
                      <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                        {message.content}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}

              {sending ? (
                <Box
                  sx={{
                    alignSelf: "flex-start",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Réflexion…
                  </Typography>
                </Box>
              ) : null}
            </Box>

            <Box
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                void sendPrompt();
              }}
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-end",
                p: 1.5,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <TextField
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={4}
                size="small"
                placeholder="Écris ton message…"
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void sendPrompt();
                  }
                }}
                disabled={sending}
              />
              <IconButton
                type="submit"
                color="primary"
                disabled={sending || !prompt.trim()}
                aria-label="Envoyer"
              >
                <SendIcon />
              </IconButton>
            </Box>

            {error ? (
              <Typography
                variant="caption"
                color="error"
                sx={{ px: 2, pb: 1.25, display: "block" }}
              >
                {error}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChatbotWidget;
